import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { FEATURES } from '../services/features';
import { USE_SUPABASE, supabase } from '../services/supabaseClient';

// Backup plan (2026-10-22): the site whose database is the COPY says so at the top of every page. A copy refuses
// every change (0138); without this line the refusals would look like bugs. The same line shows on the main
// site while the backup is in charge (the main is fenced then), so it says "view only", never "this is the backup
// copy" (the short wording of dc79733d said so on the fenced main too), and links to the other site. Asks once a
// minute; no answer = no banner.
const EVERY_MS = 60_000;
const BACKUP_SITE = 'https://wolfdrivers.net';
/** The backup site's bundle reads the backup database (backup-server/deploy: VITE_SUPABASE_URL). */
const BACKUP_API_HOST = 'api.wolfdrivers.net';

/** Where changes are made while this site is view only: the other site. */
export function otherSite(): { href: string; label: string } {
  let host = '';
  try { host = new URL(String(import.meta.env.VITE_SUPABASE_URL || '')).hostname; } catch { /* not a URL */ }
  if (host !== BACKUP_API_HOST) return { href: BACKUP_SITE, label: 'the backup site (wolfdrivers.net)' };
  return import.meta.env.VITE_IS_STAGING === 'true'
    ? { href: 'https://stagingwolfdriver.netlify.app', label: 'the main site (stagingwolfdriver.netlify.app)' }
    : { href: 'https://wolfdrivers.com', label: 'the main site (wolfdrivers.com)' };
}

/** This bundle reads the backup database: the backup site (wolfdrivers.net). */
function onBackupSite(): boolean {
  try { return new URL(String(import.meta.env.VITE_SUPABASE_URL || '')).hostname === BACKUP_API_HOST; } catch { return false; }
}

export const BackupCopyBanner: React.FC = () => {
  const [copy, setCopy] = useState(false);
  // The backup took over the DATABASE only (0141 scope): staff stay on the main site, which now uses this same
  // database — the backup site is not in use, and its server answers no function call (one server writes).
  const [notInUse, setNotInUse] = useState(false);
  useEffect(() => {
    if (!FEATURES.backupSync || !USE_SUPABASE) return undefined;
    let alive = true;
    const check = async () => {
      try {
        const { data, error } = await supabase().rpc('wolf_site_state');
        if (alive && !error) {
          const site = data as { state?: string; scope?: string } | null;
          setCopy(site?.state === 'copy');
          setNotInUse(onBackupSite() && site?.state === 'in_charge' && site?.scope === 'database');
        }
      } catch { /* no answer: leave the banner as it was */ }
    };
    void check();
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') void check(); }, EVERY_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, []);
  if (notInUse) {
    const main = otherSite();
    return (
      <div role="status" className="shrink-0 bg-amber-100 text-amber-900 border-b border-amber-200 px-4 py-2 text-sm flex items-center gap-2" data-testid="backup-copy-banner">
        <Eye size={16} className="shrink-0" />
        <span>
          <strong>Not in use</strong> — the backup database is live, and staff work on{' '}
          <a href={main.href} className="underline font-medium">{main.label}</a>.
        </span>
      </div>
    );
  }
  if (!copy) return null;
  const other = otherSite();
  return (
    <div role="status" className="shrink-0 bg-amber-100 text-amber-900 border-b border-amber-200 px-4 py-2 text-sm flex items-center gap-2" data-testid="backup-copy-banner">
      <Eye size={16} className="shrink-0" />
      <span>
        <strong>View only</strong> — make changes on{' '}
        <a href={other.href} className="underline font-medium">{other.label}</a>.
      </span>
    </div>
  );
};
